import { Inject } from '@odinjs/odin';

import { getFilterDates, isContrato } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import { phoneNumberFormatter } from '@philips-emr/hpms-stipulator-commons/lib/formatters/phone-number-formatter';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent } from '@philips-emr/hpms-stipulator-components';
import { HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { DeckAPI, FetchAPI, FieldAPI, FormAPI, PanelAPI } from '@philips-emr/tws-components';
import Controller, { OnAfterFetch, OnBeforeFetch, OnClick, OnDynamicContentRender, OnMount } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { isEmptyArrayOrObject, isEmptyObject, isEmptyOrBlankString, isValidURL } from '@philips-emr/tws-utils';

import { DashboardAliases as Aliases, FragmentNames } from '../../codegen';
import BaseMensalidadeCustomCardController, { Mensalidade } from '../mensalidades/base-mensalidade-custom-card-controller';
import './dashboard-controller.css';

interface InfoPlano {
  dsLinkRolProc: string;
  dsLinkGuiaMedico: string;
}

interface MensalidadeData {
  nrNotaFiscal: number;
}

interface Operadora {
  nrTelefoneOperadoraFormatada: string;
  nrDddTelefoneOperadora: string;
  nrTelefoneAns: string;
  dsEmailAns: string;
  dsSiteAns: string;
  dsLinkNucleoFisc: string;
  nmOperadora: string;
}

@Controller({ fragment: FragmentNames.DASHBOARD })
export default class DashboardController extends BaseMensalidadeCustomCardController {

  private readonly PERMISSION_MENSALIDADE = '9122_5';

  @Inject(Aliases.DECK_MENSALIDADES)
  private deckMensalidade: DeckAPI;

  @Inject(Aliases.PANEL_MENSALIDADE)
  private panelMensalidade: PanelAPI;

  @Inject(Aliases.PANEL_ROL_PROCEDIMENTO)
  private panelRolProcedimento: PanelAPI;

  @Inject(Aliases.PANEL_GUIA_MEDICO)
  private panelGuiaMedico: PanelAPI;

  @Inject(Aliases.PANEL_INFO_PLANO)
  private panelInfoPlano: PanelAPI;

  @Inject(Aliases.FIELD_NOTA_FISCAL)
  private fieldNotaFiscal: FieldAPI;

  @Inject(Aliases.FORM_OPS_PHILIPS)
  private formOpsPhilips: FormAPI;

  private linkRolProcedimento: string | null;

  private linkGuiaMedico: string | null;

  @OnMount(Aliases.PANEL_MENSALIDADE)
  public onMountFetchMensalidade(event: MountEvent): void {
    const api = event.target as unknown as PanelAPI;

    if (isContrato() && this.hasPermissionMensalidade()) {
      super.onMountFetchMensalidades();
    } else {
      api.setVisible(false);
    }
  }

  @OnBeforeFetch(Aliases.FETCH_MENSALIDADES)
  public onBeforeFetch(event: FetchEvent): void {
    const fetch = event.target as unknown as FetchAPI;
    fetch.setParameters({ ...fetch.getParameters(), ...getFilterDates('dtInicio', 'dtFim', 46), limit: 1 });
  }

  @OnAfterFetch(Aliases.FETCH_MENSALIDADES)
  public onAfterFetch(event: FetchEvent): void {
    this.deckMensalidade.setDone(true);

    if (event.data === undefined) {
      return;
    }

    this.context.set({ hasMensalidade: !isEmptyArrayOrObject(event.data) });
    this.panelMensalidade.setVisible(!isEmptyArrayOrObject(event.data));

    const mensalidade = event.data as MensalidadeData;

    if (!mensalidade?.nrNotaFiscal) {
      this.fieldNotaFiscal.setVisible(false);
    }

  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_SITUACAO)
  public onDynamicContentRenderCardLote(event: RenderEvent): void {
    super.onDynamicContentRenderCardLote(event);
  }

  @HPMSOnCreateOption(Aliases.HPMS_CUSTOM_CARD_MENSALIDADES)
  public onCreateOption(event: HpmsCustomCardMenuRenderEvent<Mensalidade>): void {
    super.onCreateOption(event);
  }

  @HPMSOnOptionClick(Aliases.HPMS_CUSTOM_CARD_MENSALIDADES)
  public onOptionClick(event: HpmsCustomCardEvent<Mensalidade>): void {
    super.onOptionClick(event);
  }

  @OnAfterFetch(Aliases.FETCH_PLANO)
  public onAfterFetchInfoPlano(event: FetchEvent): void {
    if (!event.data) {
      return;
    }

    const data = event.data as unknown as InfoPlano;

    if (isEmptyObject(data)) {
      this.panelInfoPlano.setVisible(false);
      return;
    }

    if (isEmptyOrBlankString(data.dsLinkRolProc)) {
      this.panelRolProcedimento.setVisible(false);
    } else {
      this.linkRolProcedimento = data.dsLinkRolProc;
    }

    if (isEmptyOrBlankString(data.dsLinkGuiaMedico)) {
      this.panelGuiaMedico.setVisible(false);
    } else {
      this.linkGuiaMedico = data.dsLinkGuiaMedico;
    }

  }

  @OnClick(Aliases.LINK_ROL_PROCEDIMENTO)
  public onClickLinkRolProcedimento(): void {
    this.goto(this.linkRolProcedimento);
  }

  @OnClick(Aliases.LINK_GUIA_MEDICO)
  public onClickLinkGuiaMedico(): void {
    this.goto(this.linkGuiaMedico);
  }

  private goto(url: string | null): void {
    if (!url) {
      return;
    }

    if (isValidURL(url)) {
      window.open(url);
      return;
    }

  }

  private getCustomWidth(): number {
    return this.hasPermissionMensalidade() ? 6 : 12;
  }

  @OnAfterFetch(Aliases.FETCH_OPERADORA)
  public onAfterFetchOperadora(event: FetchEvent): void {
    if (!event.data) {
      return;
    }

    const data = event.data as Operadora;

    if (data.nmOperadora) {
      this.formOpsPhilips.setTitle(data.nmOperadora);
    }

    if (!isEmptyOrBlankString(data.nrTelefoneOperadoraFormatada)) {
      if (!data.nrDddTelefoneOperadora || data.nrDddTelefoneOperadora === '080') {
        data.nrTelefoneOperadoraFormatada = phoneNumberFormatter(data.nrTelefoneOperadoraFormatada, {}, true);
      } else {
        data.nrTelefoneOperadoraFormatada = phoneNumberFormatter(data.nrTelefoneOperadoraFormatada, {}, false);
      }
    }

    if (!isEmptyOrBlankString(data.nrTelefoneAns)) {
      data.nrTelefoneAns = phoneNumberFormatter(data.nrTelefoneAns, {}, true);
    }

  }

  private hasPermissionMensalidade(): boolean {
    return StipulatorContext.isAllowedTwsFunction(this.PERMISSION_MENSALIDADE);
  }

  @OnMount([Aliases.FIELD_ANS, Aliases.FIELD_NUCLEO])
  public onMountDsLinkNucleoFisc(event: MountEvent): void {
    const api = event.target as FieldAPI;
    api.setWidth(this.getCustomWidth());
  }

}
